/**
 * Arriving with a ticket instead of a password (B2B §7.1).
 *
 * A platform admin clicks a customer in our console and this dashboard opens
 * beside it — a second browser tab, or a second window in the desktop app.
 * What the click carries across is a one-time ticket in the URL's *fragment*,
 * which the browser never sends to a server and no access log ever sees; this
 * module takes it out of the address bar and trades it for the session that
 * tab will run on.
 *
 * The session belongs to the tab, not to the browser (`markHandedSession`):
 * two customers open at once are two sessions, and neither may overwrite the
 * other or the admin's own.
 */

import { api } from "./client";
import { markHandedSession } from "./http";

/** `…/#s=<ticket>`. A fragment rather than a query so the ticket stays out of
 *  the request line, and a name of one letter because it is read by machines
 *  and glanced at by us. */
const TICKET = /(?:^|&)s=([^&]*)/;

/**
 * The ticket this page was opened with, taken *out* of the URL as it is read.
 *
 * Stripped immediately and unconditionally: it is spent a moment later either
 * way, and a URL that cannot be copied out of the address bar cannot be pasted
 * anywhere at all — into a bug report, a chat window, or the wrong tab.
 */
export function takeTicket(): string | null {
  const match = TICKET.exec(window.location.hash.replace(/^#/, ""));
  if (!match) return null;
  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search,
  );
  return decodeURIComponent(match[1]);
}

let refusal: unknown = null;

/** Why the handoff failed, for the login screen this tab falls back to. */
export function handoffRefusal(): unknown {
  return refusal;
}

/**
 * Trade the ticket for this tab's session. Never rejects: a ticket that has
 * expired, been spent, or names an account that has since been closed leaves
 * the tab on the login screen with the reason on it, which is a screen that
 * already knows how to say all three (`signInMessage`).
 */
export async function redeemTicket(ticket: string): Promise<void> {
  // Marked before the token exists, and before the failure path too: a tab
  // opened for a customer must never fall back to whatever session this
  // browser happens to hold for somebody else.
  markHandedSession();
  try {
    await api.consoleSession(ticket);
  } catch (error) {
    refusal = error;
  }
}
